/* Site-wide search. Vehicle selection remains independent for fitment and View parts. */
class RivalSearchMetadata extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('[data-search-part-number]')) this.highlightPartNumber();
    else queueMicrotask(() => { if (this.isConnected) this.highlightPartNumber(); });
  }
  highlightPartNumber() {
    const normalize = (value) => value.toUpperCase().replace(/[\s\-\u2010-\u2015\u2212]/g, '');
    const query = normalize(this.dataset.searchQuery || '');
    this.querySelectorAll('[data-search-part-number]').forEach((node) => {
      const original = node.textContent;
      node.replaceChildren(document.createTextNode(original));
      node.removeAttribute('data-search-part-match');
      if (query.length < 3) return;
      if (normalize(original) !== query) return;
      node.replaceChildren();
      node.dataset.searchPartMatch = 'exact';
      const mark = document.createElement('mark');
      mark.className = 'rival-search-part-match';
      mark.textContent = original;
      node.append(mark);
    });
  }
}
if (!customElements.get('rival-search-metadata')) customElements.define('rival-search-metadata', RivalSearchMetadata);
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    this.cachedMap = new Map();
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.searchContent = this.querySelector('.search__content');
    this.results = this.querySelector('[role="listbox"]');
    this.status = this.querySelector('[data-search-status]');
    this.errorPanel = this.querySelector('[data-search-error]');
    this.baseErrorText = this.errorPanel?.querySelector('p')?.textContent || '';
    this.header = this.closest('header');
    this.basePlaceholder = this.input.placeholder;
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.input.addEventListener('focus', () => this.stopExamples(), options);
    this.input.addEventListener('blur', () => this.startExamples(), options);
    this.motionPreference.addEventListener('change', () => this.startExamples(), options);
    document.addEventListener('visibilitychange', () => this.startExamples(), options);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') this.classList.add('rival-search--keyboard');
    }, options);
    this.addEventListener('pointerdown', (event) => {
      this.pointerInside = true;
      this.classList.remove('rival-search--keyboard');
      const link = event.target?.closest?.('a[data-search-option]');
      // Keep the keyboard and result positions stable until the native link click.
      // Canceling pointerdown prevents focus transfer, while click and touch scrolling remain native.
      if (link && this.results?.contains(link) && this.active && document.activeElement === this.input &&
          event.button === 0 && event.isPrimary !== false && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        event.preventDefault();
      }
    }, options);
    document.addEventListener('pointerup', () => {
      clearTimeout(this.pointerTimer);
      this.pointerTimer = setTimeout(() => { this.pointerInside = false; }, 0);
    }, options);
    document.addEventListener('pointercancel', () => { this.pointerInside = false; }, options);
    this.startExamples();
    this.active = false;
    this.requestVersion = 0;
    this.form.addEventListener('submit', (event) => { if (!this.getQuery()) event.preventDefault(); }, options);
    this.querySelector('button[type="reset"]').addEventListener('click', (event) => {
      event.preventDefault();
      this.input.value = '';
      this.cancelRequest();
      this.removeAttribute('results');
      if (this.errorPanel) this.errorPanel.hidden = true;
      this.clearSelection();
      this.status.textContent = '';
      this.input.focus();
      this.open();
      this.requestResults();
    }, options);
    this.querySelector('[data-search-close]')?.addEventListener('click', () => {
      this.close();
      this.input.blur();
    }, options);
    if (this.dataset.searchEnabled === 'false' || !this.searchContent) return;
    this.input.addEventListener('focus', () => {
      if (this.suppressFocusOpen) return;
      this.open();
      if (this.getQuery()) this.requestResults();
    }, options);
    this.input.addEventListener('click', () => {
      if (this.active) return;
      this.open();
      if (this.getQuery()) this.requestResults();
    }, options);
    this.input.addEventListener('input', (event) => {
      this.cancelRequest();
      this.clearSelection();
      this.removeAttribute('results');
      this.errorPanel.hidden = true;
      this.open();
      if (!this.getQuery()) { this.requestResults(); return; }
      this.setLoading(true);
      if (!event.isComposing) this.timer = setTimeout(() => this.requestResults(), 200);
    }, options);
    this.input.addEventListener('compositionend', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.requestResults(), 200);
    }, options);
    this.input.addEventListener('keydown', (event) => this.onKeydown(event), options);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.active) {
        event.preventDefault();
        this.suppressFocusOpen = true;
        this.close();
        this.input.focus({ preventScroll: true });
        this.suppressFocusOpen = false;
      }
    }, options);
    this.addEventListener('focusout', (event) => {
      // The browser can report the body as active before the next control receives focus.
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      queueMicrotask(() => {
        const focused = document.activeElement;
        // Mobile keyboard dismissal can blur to the page before a result tap becomes a click.
        // Keep native links available; an outside click or focus on another control still closes search.
        const neutralBlur = !event.relatedTarget && (!focused || focused === document.body || focused === document.documentElement);
        if (!this.pointerInside && !this.contains(focused) && !neutralBlur) this.close();
      });
    }, options);
    document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); }, options);
    document.addEventListener('menu-drawer:open', () => this.close(), options);
    window.addEventListener('resize', () => this.updateSpace(), options);
    window.visualViewport?.addEventListener('resize', () => this.updateSpace(), options);
    window.visualViewport?.addEventListener('scroll', () => this.updateSpace(), options);
  }
  disconnectedCallback() { this.close(); this.stopExamples(); clearTimeout(this.pointerTimer); this.listeners?.abort(); }
  stopExamples() {
    clearTimeout(this.exampleTimer);
    if (this.input) this.input.placeholder = this.basePlaceholder;
  }
  startExamples() {
    this.stopExamples();
    if (!this.header || this.getQuery() || document.activeElement === this.input || document.hidden) return;
    const examples = (this.input.clientWidth < 420 ? this.dataset.shortExamples : this.dataset.examples)?.split('|').filter(Boolean);
    if (!examples?.length) return;
    const prefix = this.dataset.examplePrefix || '';
    if (this.motionPreference.matches) { this.input.placeholder = prefix + examples[0]; return; }
    this.input.placeholder = prefix;
    let index = 0;
    let length = 0;
    let deleting = false;
    const tick = () => {
      if (!this.isConnected || this.getQuery() || document.activeElement === this.input || document.hidden) { this.stopExamples(); return; }
      length += deleting ? -1 : 1;
      this.input.placeholder = prefix + examples[index].slice(0, length);
      let delay = deleting ? 35 : 70;
      if (length === 0) { index = (index + 1) % examples.length; deleting = false; delay = 180; }
      else if (length === examples[index].length && !deleting) { deleting = true; delay = 4000; }
      this.exampleTimer = setTimeout(tick, delay);
    };
    this.exampleTimer = setTimeout(tick, 800);
  }
  getQuery() { return this.input.value.trim(); }
  updateSpace() {
    if (!this.active) return;
    const viewport = window.visualViewport;
    this.style.setProperty('--rival-search-viewport-height', `${viewport?.height || window.innerHeight}px`);
    this.style.setProperty('--rival-search-viewport-top', `${viewport?.offsetTop || 0}px`);
    const bottom = (viewport?.height || window.innerHeight) + (viewport?.offsetTop || 0);
    const fieldBottom = this.input.getBoundingClientRect().bottom;
    this.style.setProperty('--rival-search-available-height', `${Math.max(100, bottom - fieldBottom)}px`);
  }
  open() {
    if (!this.searchContent || this.dataset.searchEnabled === 'false') return;
    const opening = !this.active;
    document.querySelectorAll('predictive-search').forEach((other) => { if (other !== this && other.active) other.close?.(); });
    this.active = true;
    this.classList.add('predictive-search-open');
    this.searchContent.classList.remove('hidden');
    this.input.setAttribute('aria-expanded', 'true');
    if (this.header) document.body.classList.add('search-open');
    this.updateSpace();
    if (opening && !this.getQuery()) this.requestResults();
  }
  close() {
    this.active = false;
    this.cancelRequest();
    this.clearSelection();
    this.classList.remove('predictive-search-open');
    this.input?.setAttribute('aria-expanded', 'false');
    this.searchContent?.classList.add('hidden');
    if (this.header) document.body.classList.remove('search-open');
  }
  cancelRequest() { clearTimeout(this.timer); this.fetchController?.abort(); this.pendingKey = null; this.requestVersion++; this.setLoading(false); }
  setLoading(loading) {
    this.toggleAttribute('loading', loading);
    this.results?.setAttribute('aria-busy', String(loading));
    if (loading && this.status) this.status.textContent = this.dataset.loadingText;
  }
  async requestResults() {
    const query = this.getQuery();
    if (!this.active) return;
    if (!query) {
      this.cancelRequest();
      this.removeAttribute('results');
      this.errorPanel.hidden = true;
      this.querySelector('.search__recommendations').hidden = false;
      this.status.textContent = '';
      this.updateSpace();
      return;
    }
    const requestKey = query;
    if (this.pendingKey === requestKey) return;
    this.cancelRequest();
    this.pendingKey = requestKey;
    const version = this.requestVersion;
    this.errorPanel.hidden = true;
    this.querySelector('.search__recommendations').hidden = false;
    const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,query,page,article');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    const key = url.toString();
    this.setLoading(true);
    this.fetchController = new AbortController();
    const signal = this.fetchController.signal;
    try {
      const catalogueRequest = window.RivalCatalogueSearch?.page(query, { signal, limit: 5 }).then(page => ({ page }), error => ({ error }));
      let text = this.cachedMap.get(key);
      if (!text) {
        const response = await fetch(key, { signal });
        if (!response.ok) throw new Error('Search unavailable');
        text = await response.text();
        if (this.cachedMap.size >= 30) this.cachedMap.delete(this.cachedMap.keys().next().value);
        this.cachedMap.set(key, text);
      }
      if (!this.active || version !== this.requestVersion || query !== this.getQuery()) return;
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const content = parsed.getElementById(this.results.id);
      if (!content) throw new Error('Search response unavailable');
        const response = await catalogueRequest;
        if (!response || response.error) throw response?.error || new Error('Search unavailable');
        const page = response.page;
          const cards = await window.RivalCatalogueSearch.render(page.items, 'predictive', query, signal, this.results.id);
          if (!this.active || version !== this.requestVersion || query !== this.getQuery()) return;
          const group = content.querySelector('.rival-search-products');
          if (!group) throw new Error('Search response unavailable');
          if (group) {
            const heading = group.querySelector('.rival-search-heading');
            group.replaceChildren(); if (heading) group.append(heading);
            if (cards.length) { const list = document.createElement('ul'); list.className = 'rival-search-list'; list.append(...cards); group.append(list); }
            else group.append(window.RivalCatalogueSearch.emptyState(this, query));
          }
      this.toggleAttribute('data-search-empty-state', page.total === 0);
      if (!this.active || version !== this.requestVersion || query !== this.getQuery()) return;
      this.results.innerHTML = content.innerHTML;
      this.results.querySelectorAll('rival-search-metadata').forEach((metadata) => metadata.highlightPartNumber());
      const products = this.results.querySelector('.rival-search-products .rival-search-list');
      if (products) {
        const exact = [...products.children].filter((item) => item.querySelector('[data-search-part-match="exact"]'));
        if (exact.length) products.prepend(...exact);
      }
      this.setAttribute('results', 'true');
      this.errorPanel.hidden = true;
      this.status.textContent = this.dataset.resultsText;
      this.clearSelection();
      this.updateSpace();
    } catch (error) {
      if (error.name === 'AbortError' || version !== this.requestVersion || !this.active) return;
      this.removeAttribute('results');
      this.errorPanel.hidden = false;
      this.querySelector('.search__recommendations').hidden = true;
      this.results.replaceChildren();
      const guidance = window.RivalCatalogueSearch?.emptyState?.(this, query, { unavailable: true });
      if (guidance) this.errorPanel.replaceChildren(guidance);
      else this.errorPanel.querySelector('p').textContent = this.baseErrorText;
      this.status.textContent = this.baseErrorText;
    } finally {
      if (version === this.requestVersion) { this.pendingKey = null; this.setLoading(false); }
    }
  }
  clearSelection() {
    this.selected = null;
    this.input?.removeAttribute('aria-activedescendant');
    this.results?.querySelectorAll('[aria-selected="true"]').forEach((item) => item.setAttribute('aria-selected', 'false'));
    const starter = this.querySelector('.search__recommendations');
    if (starter) starter.hidden = false;
  }
  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Enter' && this.active && this.selected) { event.preventDefault(); this.selected.querySelector('a').click(); return; }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !this.hasAttribute('results')) return;
    this.classList.add('rival-search--keyboard');
    event.preventDefault();
    this.open();
    const options = [...this.results.querySelectorAll('[role="option"]')].filter((item) => item.getClientRects().length);
    if (!options.length) return;
    const position = options.indexOf(this.selected);
    const next = event.key === 'ArrowDown' ? (position + 1) % options.length : (position <= 0 ? options.length - 1 : position - 1);
    this.clearSelection();
    this.selected = options[next];
    this.selected.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', this.selected.id);
    this.selected.scrollIntoView({ block: 'nearest' });
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
